import { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';
  const [activeCategory, setActiveCategory] = useState(slug || 'remenzixun');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  const filteredArticles = useMemo(() => {
    let result = articles.filter((a) => a.category === activeCategory);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = articles.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.summary.toLowerCase().includes(q) ||
          a.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    return result;
  }, [activeCategory, searchQuery]);

  const seoTitle = searchQuery
    ? `搜索"${searchQuery}"的结果`
    : currentCategory
      ? `${currentCategory.name}`
      : '分类浏览';

  const seoDescription = currentCategory
    ? `浏览51网址导航${currentCategory.name}分类下的所有文章和资讯，${searchQuery ? `正在搜索"${searchQuery}"相关内容。` : '获取最新最全的内容更新。'}`
    : '浏览51网址导航所有分类的文章和资讯。';

  return (
    <>
      <SEOHead
        title={`${seoTitle} - 51网址导航`}
        description={seoDescription}
        keywords={`${currentCategory?.name || '分类'}, 51网址导航, 网址导航, 上网导航, 在线导航${searchQuery ? `, ${searchQuery}` : ''}`}
        canonicalUrl={`/category/${activeCategory}${searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : ''}`}
      />

      <div className="min-h-screen bg-background-50">
        <Header />
        <main className="pt-20 pb-10">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="mb-4">
                <Breadcrumb
                  items={[
                    { label: '首页', href: '/' },
                    { label: currentCategory?.name || '分类' },
                  ]}
                />
              </div>

              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
                {searchQuery ? `搜索"${searchQuery}"的结果` : currentCategory?.name}
              </h1>
              {searchQuery && (
                <p className="text-sm text-foreground-500 mb-6">
                  找到 {filteredArticles.length} 篇相关内容
                </p>
              )}

              <div className="flex flex-wrap items-center gap-2 mb-8">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeCategory === cat.slug && !searchQuery
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              {filteredArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300 overflow-hidden transition-all cursor-pointer"
                    >
                      <div className="relative w-full h-48 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <h3 className="text-base font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors leading-relaxed">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
                          {article.summary}
                        </p>
                        <div className="flex items-center justify-between text-xs text-foreground-400">
                          <span className="flex items-center gap-1">
                            <i className="ri-user-line"></i>
                            {article.author}
                          </span>
                          <span>{article.date}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 flex items-center justify-center bg-background-200 rounded-full mx-auto mb-4">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">
                    {searchQuery ? `未找到与"${searchQuery}"相关的文章` : '该分类暂无文章'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}